import React from 'react';
import type { TemplateItem } from './template-gallery';

type Props = {
  open: boolean;
  onClose: () => void;
  onInsert: (item: TemplateItem) => void | Promise<void>;
};

const NewTemplateGalleryTry: React.FC<Props> = ({ open, onClose, onInsert }) => {
  const [items, setItems] = React.useState<TemplateItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');
  const [sort, setSort] = React.useState<'new'|'old'|'name'|'size'>('new');
  const [zoom, setZoom] = React.useState<number>(50); // percent

  const load = React.useCallback(async () => {
    if (!open) return;
    setLoading(true); setError('');
    const BASES = [''];
    const PATHS = ['/get_templates.php', '/list_templates.php'];
    let lastErr: string = '';
    for (const p of PATHS) {
      for (const b of BASES) {
        try {
          const url = b ? `${b}${p}` : p;
          const r = await fetch(url, { credentials: 'include', cache: 'no-store' });
          if (!r.ok) { lastErr = `HTTP ${r.status}`; try { lastErr += ` ${await r.text()}`; } catch {} continue; }
          const j = await r.json();
          const raw: TemplateItem[] = Array.isArray(j.items) ? j.items : [];
          const base = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
          const fixed = raw.map((it) => ({ ...it, url: (it.url || '').startsWith('http') ? it.url : `${base}${it.url}` }));
          setItems(fixed);
          setLoading(false);
          return;
        } catch (e: any) {
          lastErr = e?.message || 'network error';
          continue;
        }
      }
    }
    setError(`Failed to load templates${lastErr ? `: ${lastErr}` : ''}`);
    setLoading(false);
  }, [open]);

  React.useEffect(() => { load(); }, [load]);

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    let arr = items.filter(it => !needle || it.name.toLowerCase().includes(needle));
    arr = arr.slice().sort((a,b)=>{
      if (sort==='new') return (b.mtime||0) - (a.mtime||0);
      if (sort==='old') return (a.mtime||0) - (b.mtime||0);
      if (sort==='name') return a.name.localeCompare(b.name);
      if (sort==='size') return (b.size||0) - (a.size||0);
      return 0;
    });
    return arr;
  }, [items, q, sort]);

  const scale = Math.max(10, Math.min(100, zoom)) / 100;
  const invPercent = (100 / scale).toFixed(3) + '%';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4" aria-modal="true" role="dialog">
        <div className="relative w-full max-w-6xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-3">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </span>
              Template Gallery
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5">{filtered.length} files</span>
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:ring-2 focus:ring-emerald-400" />
              <select value={sort} onChange={e=>setSort(e.target.value as any)} className="rounded-md border border-gray-300 px-2 py-1 text-xs">
                <option value="new">Newest</option>
                <option value="old">Oldest</option>
                <option value="name">Name A–Z</option>
                <option value="size">Size</option>
              </select>
              <div className="hidden sm:flex items-center gap-2 ml-2">
                <span className="text-gray-500">Zoom</span>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={5}
                  value={zoom}
                  onChange={(e)=> setZoom(parseInt(e.target.value,10) || 50)}
                  className="w-40 accent-emerald-500"
                  aria-label="Zoom"
                />
                <span className="w-10 text-right tabular-nums">{zoom}%</span>
              </div>
              <button className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={load}>Refresh</button>
              <button className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={onClose}>Close</button>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-auto p-3">
            {error && <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {Array.from({length:8}).map((_,i)=> (
                  <div key={i} className="rounded-lg border border-gray-200 bg-gray-50">
                    <div className="aspect-video w-full animate-pulse bg-gray-100" />
                    <div className="border-t border-gray-200 p-2 h-10" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filtered.map((it, idx) => (
                  <div key={idx} className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <div className="relative bg-gray-50">
                      <div className="aspect-video w-full overflow-hidden">
                        <div style={{ width: invPercent, height: invPercent, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                          <iframe title={it.name} src={it.url} className="h-full w-full" sandbox="allow-scripts allow-same-origin allow-forms" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-2">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-gray-800" title={it.name}>{it.name}</div>
                        <div className="text-[10px] text-gray-500">{Math.round((it.size||0)/1024)} KB</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50" href={it.url} target="_blank" rel="noreferrer">Open</a>
                        <button className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700 hover:bg-emerald-100" onClick={()=> onInsert(it)}>Insert</button>
                      </div>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && !loading && !error && (
                  <div className="col-span-full rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">No templates found. Add .html files under <span className="font-mono">storage/uploads/media/templates</span>.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTemplateGalleryTry;
